import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  coverImage,
  topCanciones,
  cancionesSolicitadas,
  scoreAcciones,
  reglasCanciones,
  globalStats,
  estadoLabels,
} from '@/mocks/cancionesSolicitadas';
import type { EstadoCancion } from '@/mocks/cancionesSolicitadas';

type Ordenamiento = 'mas-pedidas' | 'ultimas-pedidas' | 'top-canciones' | 'al-aire' | 'pendientes';

export default function CancionesSolicitadasPage() {
  const [ordenamiento, setOrdenamiento] = useState<Ordenamiento>('mas-pedidas');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoCancion | 'todos'>('todos');
  const [vista, setVista] = useState<'canciones' | 'reglas'>('canciones');

  const ordenadas = (() => {
    let base = [...cancionesSolicitadas];
    if (estadoFiltro !== 'todos') {
      base = base.filter((c) => c.estado === estadoFiltro);
    }
    switch (ordenamiento) {
      case 'mas-pedidas':
        return base.sort((a, b) => b.score - a.score);
      case 'ultimas-pedidas':
        return base.sort((a, b) => new Date(b.fechaSolicitud).getTime() - new Date(a.fechaSolicitud).getTime());
      case 'al-aire':
        return base.filter((c) => c.estado === 'al_aire').sort((a, b) => new Date(b.fechaAlAire || '').getTime() - new Date(a.fechaAlAire || '').getTime());
      case 'pendientes':
        return base.filter((c) => c.estado === 'pendiente' || c.estado === 'aceptada').sort((a, b) => new Date(b.fechaSolicitud).getTime() - new Date(a.fechaSolicitud).getTime());
      default:
        return base;
    }
  })();

  const estadoPills: { estado: EstadoCancion | 'todos'; label: string }[] = [
    { estado: 'todos', label: 'Todas' },
    { estado: 'pendiente', label: 'Pendientes' },
    { estado: 'aceptada', label: 'Aceptadas' },
    { estado: 'al_aire', label: 'Al Aire' },
    { estado: 'rechazada', label: 'Rechazadas' },
  ];

  const ordenamientos: { id: Ordenamiento; label: string; icono: string }[] = [
    { id: 'mas-pedidas', label: 'Más Pedidas', icono: 'ri-fire-line' },
    { id: 'ultimas-pedidas', label: 'Últimas Pedidas', icono: 'ri-time-line' },
    { id: 'top-canciones', label: 'Top Canciones', icono: 'ri-trophy-line' },
    { id: 'al-aire', label: 'Al Aire', icono: 'ri-broadcast-line' },
    { id: 'pendientes', label: 'Pendientes', icono: 'ri-hourglass-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
        <img
          src={coverImage}
          alt="Canciones Solicitadas"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/20 backdrop-blur-sm border border-rose-400/30 text-rose-200 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse"></span>
            CANCIONES SOLICITADAS
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            La Música la Elige la Comunidad
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-[560px] leading-relaxed">
            Pedí canciones con dedicatoria. Seguí el estado en tiempo real. Las más pedidas suben en el ranking. Todo dentro de Cabina.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStats.cancionesSolicitadasHoy} pedidas hoy
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStats.cancionesAlAireHoy} al aire
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStats.scoreTotalGenerado.toLocaleString()} score
            </span>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-4 overflow-x-auto">
            <div className="flex items-center gap-4 shrink-0">
              <Link to="/" className="flex items-center gap-1.5 text-foreground-400 hover:text-foreground-700 transition-colors">
                <i className="ri-arrow-left-line"></i>
                <span className="text-xs font-semibold">RFW</span>
              </Link>
              <div className="flex items-center gap-4">
                <span className="text-foreground-500 text-[10px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                  {globalStats.cancionesSolicitadasHoy} pedidas
                </span>
                <span className="text-foreground-500 text-[10px] flex items-center gap-1 hidden sm:inline">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  {globalStats.cancionesAlAireHoy} al aire
                </span>
                <span className="text-foreground-500 text-[10px] flex items-center gap-1 hidden md:inline">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  {globalStats.cancionesPendientes} pendientes
                </span>
                <span className="text-foreground-400 text-[10px] hidden lg:inline">
                  Top: {globalStats.topCancionSolicitada}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setVista('canciones')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  vista === 'canciones' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-music-line mr-1"></i>Canciones
              </button>
              <button
                onClick={() => setVista('reglas')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  vista === 'reglas' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-scales-line mr-1"></i>Reglas
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
        {vista === 'reglas' ? (
          /* ---- REGLAS VIEW ---- */
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">Reglas del Sistema de Canciones</h2>
              <p className="text-foreground-500 text-sm">Cómo funciona el sistema de canciones solicitadas en RFW.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {reglasCanciones.map((regla) => (
                <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                      <i className={`${regla.icono} text-background-50 text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-950 font-bold text-sm mb-2">{regla.titulo}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Score Section in Reglas */}
            <div className="mb-8">
              <h3 className="text-foreground-950 font-bold text-sm mb-4 text-center">Score por Participar</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {scoreAcciones.map((acc) => (
                  <div key={acc.id} className="bg-background-50 rounded-2xl border border-background-200 p-5 text-center">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${acc.color} flex items-center justify-center mx-auto mb-3`}>
                      <i className={`${acc.icono} text-background-50 text-lg`}></i>
                    </div>
                    <p className="text-foreground-950 font-bold text-sm mb-1">{acc.accion}</p>
                    <p className="text-foreground-500 text-xs leading-relaxed mb-3">{acc.descripcion}</p>
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-bold">
                      <i className="ri-add-line"></i>+{acc.puntos} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Summary Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Pedidas Hoy', valor: globalStats.cancionesSolicitadasHoy },
                { label: 'Al Aire Hoy', valor: globalStats.cancionesAlAireHoy },
                { label: 'Compartidos Hoy', valor: globalStats.totalCompartidosHoy },
                { label: 'Score Generado', valor: globalStats.scoreTotalGenerado.toLocaleString() },
              ].map((s) => (
                <div key={s.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
                  <p className="text-foreground-950 font-bold text-xl">{s.valor}</p>
                  <p className="text-foreground-400 text-[10px]">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ---- CANCIONES VIEW ---- */
          <div>
            {/* Ordenamiento + Filtros */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {ordenamientos.map((ord) => (
                  <button
                    key={ord.id}
                    onClick={() => setOrdenamiento(ord.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      ordenamiento === ord.id
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
                    }`}
                  >
                    <i className={ord.icono}></i>
                    {ord.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {estadoPills.map((pill) => (
                  <button
                    key={pill.estado}
                    onClick={() => setEstadoFiltro(pill.estado)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      estadoFiltro === pill.estado
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Top Canciones Section (when in top-canciones order) */}
            {ordenamiento === 'top-canciones' && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-trophy-line text-amber-500"></i>
                  <h3 className="text-foreground-950 font-bold text-sm">Top 10 Canciones Más Pedidas</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {topCanciones.map((top) => (
                    <div key={top.posicion} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center hover:border-background-300 transition-colors group cursor-pointer">
                      <div className="relative mb-3">
                        <img
                          src={top.portada}
                          alt={top.cancion}
                          className="w-full aspect-square rounded-xl object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                        <span className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 border-2 border-background-50 flex items-center justify-center text-[10px] font-bold text-background-50 shadow-sm">
                          {top.posicion}
                        </span>
                      </div>
                      <p className="text-foreground-950 font-bold text-xs leading-tight mb-0.5">{top.cancion}</p>
                      <p className="text-foreground-500 text-[10px]">{top.artista}</p>
                      <div className="flex items-center justify-center gap-3 mt-2">
                        <span className="text-foreground-400 text-[9px] flex items-center gap-1">
                          <i className="ri-music-line"></i>{top.solicitudes} pedidas
                        </span>
                        <span className="text-foreground-400 text-[9px] flex items-center gap-1">
                          <i className="ri-broadcast-line"></i>{top.vecesAlAire}x
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Main Grid - Song Requests */}
            {ordenamiento !== 'top-canciones' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ordenadas.map((req) => {
                  const estado = estadoLabels[req.estado];
                  return (
                    <div
                      key={req.id}
                      className={`bg-background-50 rounded-2xl border p-5 hover:border-background-300 transition-colors ${
                        req.estado === 'al_aire' ? 'border-emerald-200 bg-emerald-50/20' :
                        req.estado === 'rechazada' ? 'border-background-100 opacity-65' :
                        'border-background-200'
                      }`}
                    >
                      {/* Header: cover + info */}
                      <div className="flex items-start gap-3 mb-4">
                        <div className="relative shrink-0">
                          <img
                            src={req.portada}
                            alt={req.cancion}
                            className="w-14 h-14 rounded-xl object-cover"
                            loading="lazy"
                          />
                          {req.estado === 'al_aire' && (
                            <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                              <span className="w-2 h-2 bg-background-50 rounded-full animate-pulse"></span>
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-foreground-950 font-bold text-sm truncate">{req.cancion}</p>
                          </div>
                          <p className="text-foreground-500 text-xs">{req.artista}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-md ${estado.bg} ${estado.text} text-[9px] font-semibold flex items-center gap-1`}>
                              <i className={`${estado.icono} text-[9px]`}></i>
                              {estado.label}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Solicitante */}
                      <div className="flex items-center gap-2 mb-3">
                        <img
                          src={req.solicitanteAvatar}
                          alt={req.solicitante}
                          className="w-6 h-6 rounded-full object-cover"
                          loading="lazy"
                        />
                        <div>
                          <p className="text-foreground-600 text-[11px] font-medium">{req.solicitante}</p>
                          <p className="text-foreground-400 text-[9px]">{req.solicitanteCiudad}</p>
                        </div>
                      </div>

                      {/* Mensaje */}
                      <p className="text-foreground-500 text-xs leading-relaxed mb-2">
                        &ldquo;{req.mensaje}&rdquo;
                      </p>

                      {/* Dedicatoria */}
                      {req.dedicatoria && (
                        <div className="bg-rose-50/50 rounded-xl px-3 py-2 mb-3">
                          <p className="text-accent-700 text-[10px] flex items-center gap-1.5">
                            <i className="ri-heart-fill text-rose-400"></i>
                            {req.dedicatoria}
                          </p>
                        </div>
                      )}

                      {/* Motivo Rechazo */}
                      {req.motivoRechazo && (
                        <div className="bg-rose-50 rounded-xl px-3 py-2 mb-3">
                          <p className="text-rose-600 text-[10px] flex items-start gap-1.5">
                            <i className="ri-error-warning-line shrink-0 mt-0.5"></i>
                            {req.motivoRechazo}
                          </p>
                        </div>
                      )}

                      {/* Timeline */}
                      <div className="flex items-center gap-3 text-[9px] text-foreground-400 mb-3">
                        <span>Solicitada: {new Date(req.fechaSolicitud).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                        {req.fechaAlAire && (
                          <>
                            <span>&bull;</span>
                            <span className="text-emerald-600">Al Aire: {new Date(req.fechaAlAire).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </>
                        )}
                        {req.fechaAceptacion && !req.fechaAlAire && (
                          <>
                            <span>&bull;</span>
                            <span className="text-primary-600">Aceptada: {new Date(req.fechaAceptacion).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </>
                        )}
                      </div>

                      {/* Stats Footer */}
                      {req.estado !== 'rechazada' && (
                        <div className="flex items-center gap-4 pt-2 border-t border-background-100">
                          <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                            <i className="ri-share-forward-line"></i>{req.compartidos}
                          </span>
                          <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                            <i className="ri-group-line"></i>{req.participantes}
                          </span>
                          <span className="text-emerald-700 text-[10px] font-semibold ml-auto flex items-center gap-0.5">
                            <i className="ri-flashlight-fill"></i>{req.score} pts
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {ordenadas.length === 0 && ordenamiento !== 'top-canciones' && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-music-2-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">No hay canciones que mostrar con estos filtros.</p>
                <button
                  onClick={() => { setEstadoFiltro('todos'); setOrdenamiento('mas-pedidas'); }}
                  className="mt-3 px-4 py-2 rounded-lg bg-background-100 text-foreground-600 text-xs font-semibold cursor-pointer hover:bg-background-200 transition-colors"
                >
                  Limpiar Filtros
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 pb-12">
        <div className="bg-gradient-to-r from-rose-500 to-amber-500 rounded-3xl p-8 md:p-10 text-center">
          <h3 className="text-white font-bold text-lg md:text-xl mb-2">¿Querés Pedir una Canción?</h3>
          <p className="text-white/80 text-sm max-w-[500px] mx-auto mb-6 leading-relaxed">
            Entrá a la Cabina de tu radio favorita y pedí tu canción con dedicatoria. Sumá score, participá en la comunidad y hacé que tu canción llegue al aire.
          </p>
          <Link
            to="/cabina-2"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-50 text-foreground-950 font-bold text-sm cursor-pointer hover:bg-background-100 transition-all whitespace-nowrap"
          >
            <i className="ri-broadcast-line"></i>
            Ir a la Cabina
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}